/**
 * PlaceholderView Component
 *
 * Displays a placeholder for coming-soon features.
 * Shows view title, description, and "Coming Soon" badge.
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, Hammer, Lightbulb } from 'lucide-react';

/**
 * PlaceholderView displays a friendly "coming soon" message
 * @param {object} props
 * @param {string} props.title - View title
 * @param {string} props.description - Feature description
 */
function PlaceholderView({ title, description }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="text-center max-w-md">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
              <Hammer className="w-10 h-10 text-purple-600" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-amber-600" />
            </div>
          </div>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
          <Calendar className="w-4 h-4" />
          Coming Soon
        </div>

        {/* Title */}
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">
          {title}
        </h2>

        {/* Description */}
        {description && (
          <p className="text-gray-500 text-base leading-relaxed">
            {description}
          </p>
        )}

        {/* Subtle note */}
        <p className="mt-6 text-sm text-gray-500">
          This feature is under active development.
        </p>
      </div>
    </div>
  );
}

PlaceholderView.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
};

PlaceholderView.defaultProps = {
  title: 'Coming Soon',
  description: null,
};

export default PlaceholderView;
